import { formatPercent } from '@angular/common';
import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import {
  CellContextMenuEvent,
  ColDef,
  EditableCallbackParams,
  GridOptions,
  IDateFilterParams,
  IRowNode,
  RowSelectedEvent,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import moment from 'moment';
import { NGXLogger } from 'ngx-logger';

import {
  AssertionError,
  assertExists,
  getLocaleCurrencyCode,
  getLocaleCurrencySymbol,
} from 'common';
import {
  Field,
  FieldDataType,
  InboxFile,
  Permissions,
  SearchResult,
  TableFields,
} from 'models';

import { isSameMoment } from '../common/utility';
import { BadgeCellRendererComponent } from '../components/grid-cell-components/badge-cell-renderer/badge-cell-renderer.component';
import {
  SELECTION_ORDER_COLUMN_ID,
  getCellEditor,
  getCellRenderer,
  getFieldCellEditorProperties,
  getTableFieldCellRendererProperties,
} from '../components/grid-cell-components/cell-selectors';
import { DateTimeCellRendererComponent } from '../components/grid-cell-components/date-time-cell-renderer/date-time-cell-renderer.component';
import { TableFieldCellRendererComponent } from '../components/grid-cell-components/table-field-cell-renderer/table-field-cell-renderer.component';
import { ActionsMenu } from '../models/actions-menu';
import { ApplicationQuery } from '../state/application/application.query';
import { ArchivesQuery } from '../state/archives/archives.query';
import { DatabasesQuery } from '../state/databases/databases.query';
import { FieldsQuery } from '../state/fields/fields.query';

import { DecimalCellRendererComponent } from '../components/grid-cell-components/decimal-cell-renderer/decimal-cell-renderer.component';
import { AuthenticationService } from './authentication.service';

/** Grid helper service. */
@Injectable({
  providedIn: 'root',
})
export class GridHelperService {
  constructor(
    private logger: NGXLogger,
    private appQuery: ApplicationQuery,
    private databasesQuery: DatabasesQuery,
    private fieldsQuery: FieldsQuery,
    private archivesQuery: ArchivesQuery,
    private auth: AuthenticationService,
    @Inject(LOCALE_ID) private locale: string
  ) {}

  /**
   * Creates a new archive grid options object.
   *
   * @param gridOptions Grid options which will overload the defaults.
   * @returns A grid options object for use with an AG-Grid.
   */
  createArchiveGridOptions(gridOptions: Partial<GridOptions>): GridOptions {
    const options: GridOptions<SearchResult> = {
      rowSelection: {
        mode: 'multiRow',
        checkboxes: true,
        hideDisabledCheckboxes: true,
        selectAll: 'filtered',
      },
      singleClickEdit: true,
      tooltipShowDelay: 1000,
      pagination: true,
      paginationPageSize: this.appQuery.archiveResultsPerPage,
      suppressPaginationPanel: true,
      columnDefs: [], // should be populated by search results.
      editType: 'fullRow',
      enterNavigatesVertically: true,
      enterNavigatesVerticallyAfterEdit: true,
      getRowId: (params) => params.data.id.toString(),
      onFilterChanged: (event) => {
        event.api.deselectAll();
      },
    };

    Object.assign(options, gridOptions);
    return options;
  }

  /**
   * Creates a new inbox grid options object.
   *
   * @param gridOptions Grid options which will overload the defaults.
   * @param addSelectionOrderColumn Whether to add the selection order column as the first column.
   * @param editable Boolean or function returning a boolean indicating if the filename column is editable.
   * @returns A grid options object for use with an AG-Grid.
   */
  createInboxGridOptions(
    gridOptions: Partial<GridOptions>,
    addSelectionOrderColumn: boolean,
    editable: boolean | ((params: EditableCallbackParams) => boolean)
  ): GridOptions {
    const columnDefs: ColDef[] = [
      {
        field: 'filename',
        headerName: 'Filename',
        resizable: true,
        editable,
        sortable: true,
        headerCheckboxSelection: false,
        checkboxSelection: false,
      },
      {
        field: 'fileType',
        headerName: 'File Type',
        resizable: true,
        sortable: true,
      },
      {
        field: 'dateModified',
        headerName: 'Date Modified',
        resizable: true,
        sortable: true,
        cellRenderer: DateTimeCellRendererComponent,
      },
      {
        field: 'dateCreated',
        headerName: 'Date Created',
        resizable: true,
        sortable: true,
        cellRenderer: DateTimeCellRendererComponent,
      },
    ];

    if (addSelectionOrderColumn) {
      columnDefs.unshift(this.getSelectionColumn());
    }

    const options: GridOptions = {
      rowSelection: {
        mode: 'multiRow',
        checkboxes: true,
        selectAll: 'filtered',
      },
      singleClickEdit: true,
      pagination: false,
      stopEditingWhenCellsLoseFocus: true,
      columnDefs,
      getRowId: (params) => params.data.id,
      onFirstDataRendered: (params) => params.api.autoSizeAllColumns(),
      onRowDataUpdated: (params) => params.api.autoSizeAllColumns(),
      onFilterChanged: (event) => {
        event.api.deselectAll();
      },
    };

    Object.assign(options, gridOptions);
    return options;
  }

  /**
   * Determines if the archive row should be editable.
   *
   * @param params Editable callback parameters.
   * @param field Field.
   * @param archiveTableFields Array of table fields which are on the archive.
   * @param editEnabled Whether edit mode is enabled.
   * @returns Whether editting should be enabled.
   */
  editableArchiveColumn(
    params: EditableCallbackParams,
    field: Field,
    archiveTableFields: TableFields,
    editEnabled: boolean | (() => boolean)
  ): boolean {
    const permissions = params.data.permissions as Permissions;
    return (
      permissions.modifyData &&
      field.systemField === '' &&
      !field.multiValue &&
      (typeof editEnabled === 'function' ? editEnabled() : editEnabled) &&
      !archiveTableFields.some((tf) => tf.fieldIds.includes(field.id))
    );
  }

  /**
   * Determines if the inbox row should be editable.
   *
   * @param params Editable callback parameters.
   * @param editEnabled Whether edit mode is enabled.
   * @returns Whether editting should be enabled.
   */
  editableInboxRow(
    params: EditableCallbackParams,
    editEnabled: boolean
  ): boolean {
    return (
      editEnabled && (params.data.permissions as Permissions).modifyDocuments
    );
  }

  /**
   * Gets an array of AG-Grid column definitions for an archive view.
   *
   * @param archiveId Archive Id.
   * @param editEnabled Function that returns a boolean indicating if edit mode is enabled.
   * @returns An array of column definitions for AG-Grid.
   */
  getArchiveColumnDefinitions(
    archiveId: number,
    editEnabled: () => boolean
  ): ColDef[] {
    const archiveFields = this.archivesQuery.getFields(archiveId);
    const archiveTableFields = this.archivesQuery.getTableFields(archiveId);

    // Adds support for filtering our date strings by casting them to moments without the time to compare them.
    const dateFilterParameters: IDateFilterParams = {
      comparator: (fitlerDate: Date, cellValue: string) => {
        var dateAsString = cellValue;
        if (dateAsString == null) return -1;
        const cellDate = moment(moment(dateAsString));
        const filterMoment = moment(fitlerDate);

        if (cellDate.isSame(filterMoment, 'day')) {
          return 0;
        }

        if (cellDate.isBefore(filterMoment, 'day')) {
          return -1;
        }

        return 1;
      },
    };

    const newCols: ColDef[] = archiveFields
      .filter(
        (field) =>
          // Field is not a LiveField.
          !field.liveField &&
          // Hide table field columns unless the setting is enabled.
          (this.appQuery.showTableFieldColumnsInGrid ||
            !archiveTableFields.some((tableField) =>
              tableField.fieldIds.includes(field.id)
            ))
      )
      .map((field) => ({
        field: `field_${field.id}`,
        headerName: field.name,
        sortable: true,
        filter: field.type === FieldDataType.date ? 'agDateColumnFilter' : true,
        filterParams:
          field.type === FieldDataType.date ? dateFilterParameters : undefined,
        resizable: true,
        valueGetter: (params: ValueGetterParams<SearchResult>) => {
          const fieldValue = params.data?.fields.find((f) => f.id === field.id);
          if (!fieldValue) {
            this.logger.warn(
              'The search result for the document does not contain a value for the field.',
              field
            );
            return;
          }
          this.logger.debug(`${field.name} value: `, fieldValue);
          return field.multiValue
            ? fieldValue.multiValue.join(', ')
            : fieldValue.value;
        },
        valueSetter: (params: ValueSetterParams<SearchResult>) => {
          const fieldValue = params.data.fields.find((f) => f.id === field.id);
          if (!fieldValue) return false;
          if (field.multiValue) {
            fieldValue.multiValue = params.newValue.split(', ');
          } else {
            fieldValue.value = params.newValue;
          }
          return true;
        },
        editable: (params) =>
          this.editableArchiveColumn(
            params,
            field,
            archiveTableFields,
            editEnabled
          ),
        cellEditor: getCellEditor(field),
        cellEditorParams: getFieldCellEditorProperties(field),
        cellRenderer: getCellRenderer(field),
        cellStyle: {
          'text-align': `${(field.type === FieldDataType.decimal || field.type === FieldDataType.integer) && this.appQuery.rightAlignNumericValuesInGrid ? 'right' : 'left'}`,
        },
        cellRendererParams: {
          format: field.format,
          type: field.type,
        },
        comparator: (valueA, valueB) => {
          if (field.type === 2 || field.type === 4) {
            return valueA - valueB;
          }
          return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
        },
        equals:
          field.type === FieldDataType.date
            ? (oldValue, newValue) => isSameMoment(oldValue, newValue)
            : undefined,
      }));

    newCols.push(
      ...archiveTableFields.map((tableField) => ({
        field: `tablefield_${tableField.id}`,
        headerName: tableField.name,
        sortable: false,
        filter: false,
        editable: false,
        resizable: false,
        cellRenderer: TableFieldCellRendererComponent,
        cellRendererParams: getTableFieldCellRendererProperties(tableField),
      }))
    );

    return newCols;
  }

  /**
   * Formats the raw value into the appropriate date string for a grid cell.
   *
   * @param value Raw value.
   * @param momentFormat Moment format.
   * @returns The value formatted as it should be displayed to the user.
   */
  getDateTimeCellValue(value: string, momentFormat: string): string {
    if (value) {
      const date = moment(value);
      if (momentFormat) {
        return date.format(momentFormat);
      }

      return date.toDate().toLocaleString();
    }
    return '';
  }

  /**
   * Formats the raw value into the appropriate decimal string for a grid cell.
   *
   * @param value Raw value.
   * @param format Field format.
   * @returns The value formatted as it should be displayed to the user.
   */
  getDecimalCellValue(value: any, format: string): string {
    let parsedDisplayValue: string = value;
    const locale = this.appQuery.useBrowserLocaleToParseData
      ? navigator.language
      : this.locale;
    try {
      switch (format.toLowerCase()) {
        case 'c':
        case '$#,###.00':
        case '€#,###.00':
          parsedDisplayValue = this.parseAsCurrency(value, locale);
          break;
        case 'p':
          parsedDisplayValue = formatPercent(value / 100, locale);
          break;
        case 'x':
          parsedDisplayValue = Number(value).toString(16);
          break;
        case 'n':
          parsedDisplayValue = Number(value).toFixed(2);
          break;
        case 'e':
          parsedDisplayValue = Number(value).toExponential(6); // fraction digit 6 is default in knowledge base;
          break;
      }
    } catch (error) {
      this.logger.error('Unable to format decimal value.', error);
    }
    return parsedDisplayValue;
  }

  /**
   * Converts inbox files into row data for AG-Grid.
   *
   * @param files Inbox Files.
   * @returns A row data object for the grid to use.
   */
  getInboxRowData(files: InboxFile[]): any[] {
    const rowData = files.map((inboxFile) => {
      const rowItem: any = {};
      rowItem.id = `${inboxFile.filename}${inboxFile.fileType}`;
      rowItem.dateCreated = inboxFile.dateCreated;
      rowItem.dateModified = inboxFile.dateModified;
      rowItem.filename = inboxFile.filename;
      rowItem.fileType = inboxFile.fileType;
      rowItem.permissions = inboxFile.permissions;
      return rowItem;
    });

    return rowData;
  }

  /**
   * Gets the selection column definition.
   *
   * @returns A column definition.
   */
  getSelectionColumn(): ColDef {
    return {
      field: SELECTION_ORDER_COLUMN_ID,
      headerName: '#',
      headerCheckboxSelection: false,
      checkboxSelection: false,
      suppressSizeToFit: true,
      width: 110,
      lockPosition: true,
      resizable: true,
      cellClass: 'centered-grid-cell',
      cellRenderer: BadgeCellRendererComponent,
    };
  }

  /**
   * Handles row selection events.
   *
   * @param event Row selected event.
   * @param selectedRows Rows which are already selected.
   * @returns All selected rows including the one that was just selected.
   */
  onRowSelected = (
    event: RowSelectedEvent,
    selectedRows: IRowNode[]
  ): IRowNode[] => {
    this.logger.debug('On row selected event raised.', event);
    // Skip rows that are there but not displayed unless the row is being deselected in which case we always deselect it.
    if (!event.node.displayed && event.node.isSelected()) return selectedRows;
    const hasSelectionOrderColumn = !!event.api.getColumnDef(
      SELECTION_ORDER_COLUMN_ID
    );
    if (event.node.isSelected()) {
      selectedRows.push(event.node);
      if (hasSelectionOrderColumn) {
        event.node.setDataValue(SELECTION_ORDER_COLUMN_ID, selectedRows.length);
      }
    } else {
      const index = selectedRows.indexOf(event.node);
      if (index > -1) {
        selectedRows.splice(index, 1);
        if (hasSelectionOrderColumn) {
          event.node.setDataValue(SELECTION_ORDER_COLUMN_ID, '');
        }
      }
    }

    this.logger.debug('Selected rows have been ordered', selectedRows);
    return selectedRows;
  };

  /**
   * Opens the provided context menu.
   *
   * If there are selected row nodes then they are passed to the actions menu.
   * Otherwise they are pulled from the mouse event on the grid.
   *
   * @param event Mouse event.
   * @param selectedRowNodes Any selected row nodes.
   * @param actionsMenu Actions menu to open.
   */
  openContextMenu(
    contextMenuEvent: CellContextMenuEvent,
    selectedRowNodes: IRowNode[],
    actionsMenu: ActionsMenu
  ): void {
    this.logger.debug('Right clicked in grid.', contextMenuEvent);
    if (
      !contextMenuEvent.event ||
      !(contextMenuEvent.event instanceof MouseEvent)
    ) {
      this.logger.debug(
        'Cell context menu event had no mouse event, skipping.'
      );
      return;
    }

    let copyValue = '';

    // This is a field column so we need to get the cell renderer used to get the value.
    const renderers = contextMenuEvent.api.getCellRendererInstances({
      rowNodes: [contextMenuEvent.node],
      columns: [contextMenuEvent.column],
    });
    if (renderers.length > 0) {
      if (renderers[0] instanceof DateTimeCellRendererComponent) {
        copyValue = (renderers[0] as DateTimeCellRendererComponent)
          .displayValue;
      } else if (renderers[0] instanceof DecimalCellRendererComponent) {
        copyValue = (renderers[0] as DecimalCellRendererComponent).displayValue;
      } else {
        copyValue = contextMenuEvent.value;
      }
    } else {
      // The cell renderer was not returned so just use the value we have.
      this.logger.debug(
        'Cell renderer was not found for the field. The raw value in the event will be used.'
      );
      copyValue = contextMenuEvent.value;
    }

    // If a selection exists, use those rows.
    if (selectedRowNodes.length > 0) {
      this.logger.debug(
        'Trigger context menu with selected rows.',
        selectedRowNodes
      );
      actionsMenu.openMenu(
        contextMenuEvent.event,
        selectedRowNodes.map((row) => row.data),
        copyValue
      );
    } else {
      const rowNode = contextMenuEvent.node;
      this.logger.debug('Trigger context menu with target row.', rowNode);
      actionsMenu.openMenu(contextMenuEvent.event, [rowNode.data], copyValue);
    }
  }

  /**
   * Format a number as locale currency.
   *
   * @param parameterValue Value to parse.
   * @param locale Locale to be used. Defaults to the current browser locale.
   * @returns Formatted currency string.
   * @throws {Error} If unable to determine currency from locale.
   */
  private parseAsCurrency(
    parameterValue: number,
    locale = navigator.language
  ): string {
    if (!parameterValue) return '';
    const currencyCode = getLocaleCurrencyCode(locale);
    const currencySymbol = getLocaleCurrencySymbol(locale);
    try {
      assertExists(currencyCode);
      assertExists(currencySymbol);
    } catch (error) {
      throw new Error('Unable to get currency from locale.', {
        cause: error as AssertionError,
      });
    }

    return Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
    }).format(parameterValue);
  }
}
